import { Injectable } from '@angular/core'
import { IAudioCaptureStrategy } from './audio-capture-strategy.interface'
import { AudioStreamingService } from '../audio-streaming.service'
import { AudioRecordingService } from '../audio-recording.service'
import { SpeechRecognitionService } from '../speech-recognition.service'
import { AudioLevelMonitorService } from '../audio-level-monitor.service'
import { CloudSttStrategy } from './strategies/cloud-stt-strategy.service'
import { WebSpeechStrategy } from './strategies/web-speech-strategy.service'
import { MediaRecorderStrategy } from './strategies/media-recorder-strategy.service'
import { AudioFormatAdapterService } from './audio-format-adapter.service'

/**
 * Recognition mode type for audio capture strategies
 */
export type RecognitionMode = 'cloudSTT' | 'webSpeech' | 'mediaRecorder'

/**
 * Factory service for creating audio capture strategy instances
 *
 * Implements the Factory Pattern to create mode-specific audio capture strategies.
 * Each strategy wraps existing audio services (AudioStreamingService, AudioRecordingService,
 * SpeechRecognitionService) and provides a unified IAudioCaptureStrategy interface.
 *
 * Responsibilities:
 * - Create strategy instances based on recognition mode
 * - Inject required dependencies into each strategy
 * - Validate dependencies exist for requested mode
 * - Provide cleanup method for safe strategy destruction
 *
 * @example
 * ```typescript
 * // Create strategy for Cloud STT mode
 * const strategy = factory.createStrategy('cloudSTT')
 * await strategy.initialize('device-id-123')
 * await strategy.start()
 *
 * // Switch modes
 * await factory.destroyStrategy(strategy)
 * const newStrategy = factory.createStrategy('webSpeech')
 * ```
 *
 * Requirements:
 * - 2.3: Use factory pattern to instantiate mode-specific implementations
 * - 6.1: Instantiate strategy when recognition mode is selected
 * - 6.6: Provide strategy factory
 * - 6.7: Properly cleanup previous strategy when switching modes
 */
@Injectable({
  providedIn: 'root',
})
export class AudioCaptureStrategyFactory {
  constructor(
    private audioStreamingService: AudioStreamingService,
    private audioRecordingService: AudioRecordingService,
    private speechRecognitionService: SpeechRecognitionService,
    private audioLevelMonitorService: AudioLevelMonitorService,
    private audioFormatAdapterService: AudioFormatAdapterService,
  ) {}

  /**
   * Create a new audio capture strategy instance
   *
   * Factory method that instantiates the appropriate strategy based on recognition mode.
   * Each strategy receives its required dependencies via constructor injection.
   *
   * @param mode - The recognition mode to create a strategy for
   * @returns New strategy instance implementing IAudioCaptureStrategy
   * @throws Error if mode is not supported
   *
   * Requirements:
   * - 2.3: Instantiate mode-specific implementations using factory pattern
   * - 6.1: Create strategy when recognition mode is selected
   */
  createStrategy(mode: RecognitionMode): IAudioCaptureStrategy {
    switch (mode) {
      case 'cloudSTT':
        // CloudSttStrategy uses AudioStreamingService's built-in audio level monitoring
        return new CloudSttStrategy(this.audioStreamingService)

      case 'webSpeech':
        return new WebSpeechStrategy(
          this.speechRecognitionService,
          this.audioLevelMonitorService,
          this.audioFormatAdapterService,
        )

      case 'mediaRecorder':
        return new MediaRecorderStrategy(
          this.audioRecordingService,
          this.audioLevelMonitorService,
        )

      default:
        throw new Error(`Unsupported recognition mode: ${mode}`)
    }
  }

  /**
   * Destroy a strategy instance and release its resources
   *
   * Calls stop() on the strategy to ensure proper cleanup before destruction.
   * Errors during cleanup are caught and logged but not propagated.
   *
   * @param strategy - The strategy instance to destroy
   * @returns Promise that resolves when cleanup is complete
   *
   * Requirements:
   * - 6.7: Properly cleanup previous strategy before initializing new one
   */
  async destroyStrategy(strategy: IAudioCaptureStrategy): Promise<void> {
    try {
      await strategy.stop()
    } catch (error) {
      // Log error but don't propagate - cleanup should always succeed
      console.error('Error during strategy cleanup:', error)
    }
  }
}
